import { useScrollAnimation } from '../../../hooks/useScrollAnimation';

export default function EchoSection() {
  const { ref, isVisible } = useScrollAnimation();

  const features = [
    {
      icon: 'ri-lightbulb-line',
      titleJa: 'LED照明 × サウンドセンサー',
      titleEn: 'LED Lighting × Sound Sensors',
      descriptionJa: '人の声にリアルタイムで反応し、常に変化し続けるオーディオビジュアル体験を創出します。',
    },
    {
      icon: 'ri-user-voice-line',
      titleJa: 'インタラクティブ体験',
      titleEn: 'Interactive Experience',
      descriptionJa: '来場者の声や動きに応答し、空間と人を感覚的に結びつけます。',
    },
    {
      icon: 'ri-building-2-line',
      titleJa: '多様な空間に対応',
      titleEn: 'Adaptable to Various Spaces',
      descriptionJa: 'イベント、展示会、商業施設、パブリックスペースなど、あらゆる空間に設置可能です。',
    },
    {
      icon: 'ri-truck-line',
      titleJa: '日本国内レンタル対応',
      titleEn: 'Available for Rental in Japan',
      descriptionJa: 'BITMOVESが日本国内でのレンタル・設置・運用をワンストップでサポートします。',
    },
  ];

  return (
    <section
      ref={ref}
      id="echo-section"
      className="bg-gray-950 py-32 lg:py-40 px-8 lg:px-32"
    >
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div
          className={`mb-20 transition-all duration-700 ${
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}
        >
          <p className="text-xs uppercase tracking-[0.2em] text-gray-500 mb-4">
            Interactive Installation
          </p>
          <h2 className="text-5xl lg:text-6xl font-light text-white mb-6">Echo</h2>
          <p className="text-base text-gray-400 max-w-2xl">
            マルチセンサリー・インタラクティブインスタレーション、日本国内レンタル開始。
          </p>
        </div>

        {/* Main Visual */}
        <div
          className={`mb-20 transition-all duration-700 delay-200 ${
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
          }`}
        >
          <div className="w-full h-[500px] rounded-2xl overflow-hidden">
            <img
              src="https://public.readdy.ai/ai/img_res/96243cf7a7699377bdd5266eed05ce0f.jpg"
              alt="Echo interactive installation"
              className="w-full h-full object-cover object-center"
            />
          </div>
        </div>

        {/* Description */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 mb-24">
          <div
            className={`transition-all duration-700 delay-300 ${
              isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
            }`}
          >
            <h3 className="text-2xl font-medium text-white mb-6 leading-relaxed">
              声が光になる。空間が呼応する。
            </h3>
            <p className="text-base text-gray-300 leading-loose mb-6">
              「Echo」は、カナダのクリエイティブスタジオ mirari が制作した没入型インタラクティブインスタレーションです。LED照明とサウンドレスポンシブセンサーを組み合わせ、人の声にリアルタイムで反応する、常に進化し続けるオーディオビジュアル体験を創出します。
            </p>
            <p className="text-base text-gray-300 leading-loose">
              空間と感覚を通じて人と人をつなぎ、イベント・展示会・商業施設・パブリックスペースにおいて、感情的なエンゲージメントを高め、インタラクションを促進し、忘れられない印象を残します。
            </p>
          </div>
          <div
            className={`transition-all duration-700 delay-400 ${
              isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
            }`}
          >
            <p className="text-sm text-gray-500 leading-loose mb-6">
              BITMOVES is proud to launch the Japan-based rental service for{' '}
              <em>Echo</em>, an immersive interactive installation created by the
              Canadian creative studio mirari. Combining LED lighting with
              sound-responsive sensors, Echo reacts to human voice in real
              time — creating an ever-evolving audiovisual experience that
              connects people through space and sensation.
            </p>
            <p className="text-sm text-gray-500 leading-loose">
              Ideal for events, exhibitions, commercial venues, and public
              spaces, Echo enhances emotional engagement, encourages
              interaction, and leaves a lasting impression.
            </p>
          </div>
        </div>

        {/* Features Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-8 mb-24">
          {features.map((feature, index) => (
            <div
              key={index}
              className={`bg-white/5 rounded-2xl p-8 hover:bg-white/10 transition-all duration-500 ${
                isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
              }`}
              style={{
                transitionDelay: isVisible
                  ? `${index * 100 + 400}ms`
                  : '0ms',
              }}
            >
              <div className="w-12 h-12 flex items-center justify-center mb-6 bg-white/10 rounded-xl">
                <i className={`${feature.icon} text-2xl text-white`}></i>
              </div>
              <p className="text-sm font-medium text-white mb-2">{feature.titleJa}</p>
              <p className="text-xs text-gray-500 mb-4">{feature.titleEn}</p>
              <p className="text-sm text-gray-400 leading-relaxed">{feature.descriptionJa}</p>
            </div>
          ))}
        </div>

        {/* Case Study: DIG Shibuya */}
        <div
          className={`bg-white/5 rounded-2xl overflow-hidden transition-all duration-700 delay-500 ${
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
          }`}
        >
          <div className="grid grid-cols-1 lg:grid-cols-2">
            <div className="w-full h-[360px]">
              <img
                src="https://readdy.ai/api/search-image?query=Interactive%20LED%20art%20installation%20at%20night%20in%20Shibuya%20Tokyo%20urban%20venue%2C%20colorful%20light%20tubes%20responding%20to%20visitor%20voices%2C%20people%20engaging%20with%20immersive%20digital%20art%20experience%2C%20modern%20exhibition%20space%20with%20dramatic%20ambient%20lighting%2C%20professional%20event%20photography%20capturing%20the%20energy%20and%20excitement&width=800&height=500&seq=echo-dig-shibuya-007&orientation=landscape"
                alt="Echo at DIG Shibuya"
                className="w-full h-full object-cover object-center"
              />
            </div>
            <div className="p-12 lg:p-16 flex flex-col justify-center">
              <p className="text-xs uppercase tracking-wider text-gray-500 mb-4">
                Case Study
              </p>
              <h3 className="text-xl font-medium text-white mb-4 leading-relaxed">
                Echo at DIG Shibuya, Tokyo
              </h3>
              <p className="text-sm text-gray-400 leading-relaxed mb-4">
                2025年2月、東京・渋谷のDIG ShibuyaにてEchoの設置を完了。BITMOVESはソフトウェア開発、ハードウェアエンジニアリング＆製作を担当しました。
              </p>
              <p className="text-xs text-gray-500 leading-relaxed">
                Collaboration with mirari on the successful completion of Echo at
                DIG Shibuya, Tokyo. BITMOVES handled software development,
                hardware engineering &amp; production.
              </p>
              <p className="text-xs text-gray-600 mt-6">2025/2/14</p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
